import React, { useMemo } from 'react'
import { ResultObjectType, VehicleProvider } from '@cbgms/api'
import { ErrorProvider } from 'app/search/ErrorProvider'
import { IVehicleSearchFormatResults } from 'core/vehicles/mappings/vehicle'
import { useTranslation } from 'react-i18next'
import { Button } from '@mui/material'
import { StepOption, useSelectionContext } from '../context/SelectionContext'
import { useFormikContext } from 'formik'
import { useShowVehicleTypeSelector } from '../../SelectVehicleType/use-select-vehicle-type'
import { splitRegistration } from '@cbgms/base/utils/vehicle'
import { usePrimaryActionBuilder } from 'app/menu/components/Search/hooks/use-primary-action-builder'
import { ActionButtonProps, VehicleResult } from '@cbgms/components/src/top-search/search-v1/Results/VehicleResult'

export interface IResultProps extends IVehicleSearchFormatResults {
  query: string
  focus: boolean
  clearQuery: () => void
}

export const UnknownVehicleResultItem: React.FC<IResultProps> = searchResult => {
  const { setFieldValue } = useFormikContext<any>()
  const { setStep } = useSelectionContext()
  const { showSelection } = useShowVehicleTypeSelector()
  const actionBuilder = usePrimaryActionBuilder({ clearQuery: () => {} })
  const onExistingVehicleClick = useMemo(
    () => actionBuilder(searchResult.Type, searchResult.query, searchResult),
    [searchResult, actionBuilder]
  )

  const type = searchResult.Type
  switch (type) {
    case ResultObjectType.GenericVehicle:
      return (
        (searchResult.Vehicle && (
          <VehicleResult
            hasGroupHeader={false}
            Vehicle={searchResult.Vehicle}
            Type={type}
            clearQuery={searchResult.clearQuery}
            selected={searchResult.focus}
            onSelect={async vehicles => {
              const source = (vehicles.Values?.find(v => v.GenericVehicle?.Source)?.GenericVehicle?.Source || '') as VehicleProvider
              const genericVehicleOptions = vehicles.Values.map(v => v.GenericVehicle)
              const genericVehicle = await showSelection(genericVehicleOptions, source)
              const vehicle = vehicles.Values.find(v => v.GenericVehicle.ID === genericVehicle.ID)
              const { licensePlate, countryCode } = splitRegistration(vehicle?.Registration)

              setFieldValue('vehicle', vehicle)
              setFieldValue('Vin', vehicle?.Vin)
              if (licensePlate && countryCode) {
                setFieldValue('Registration', licensePlate)
                setFieldValue('Country', countryCode)
              }
              setStep(StepOption.CONFIRM)
            }}
            ActionButton={GenericVehicleActionButton}
          />
        )) ||
        null
      )
    case ResultObjectType.DatabaseVehicle:
      return (
        (searchResult.Vehicle && (
          <VehicleResult
            hasGroupHeader={false}
            Vehicle={searchResult.Vehicle}
            Type={type}
            clearQuery={searchResult.clearQuery}
            selected={searchResult.focus}
            onSelect={onExistingVehicleClick}
          />
        )) ||
        null
      )
    case ResultObjectType.Error:
      return <ErrorProvider errors={searchResult.Errors!} />
    default:
      return null
  }
}

function GenericVehicleActionButton({ hasMultipleMatches, ...rest }: ActionButtonProps) {
  const { t } = useTranslation()
  const text = hasMultipleMatches ? t('order.refine_selection') : t('vehicle.select_vehicle')
  return (
    <Button variant='outlined' sx={{ textTransform: 'none' }} {...rest} data-testid='search-select-vehicle'>
      {text}
    </Button>
  )
}
